import { Injectable } from '@angular/core';
import { CompanyResponse, CustomFields } from '@key-telematics/fleet-api-client';
import { IKuiTableCol } from 'app/key-ui';
import { escapeRqlValue } from 'app/shared/utils/rql';
import { countBy, get } from 'lodash';
import { AdminEditorService } from '../../admin.service';
import { RegisterEntityHandler } from '../../entities';
import { EntityHandler, EntityListResult, FormSectionHandler, SectionHandlerGroup } from '../entity.handler';
import { ICONS } from '../icons';
import { GridColumnHelper } from '../utils';
import { CustomFieldDetailsSectionHandler } from './details.section';
import { CustomFieldDefinitionToCustomFieldResponse, CustomFieldResopnseToCustomFieldDefinition, CustomFieldResponse, UpdateCompanyCustomFields, getCustomFieldResponseFromCompany, getEntityTypes, getTypes } from './utils';

@RegisterEntityHandler('customfield')
@Injectable()
export class CustomFieldHandler extends EntityHandler<CustomFieldResponse> {

    constructor(
        admin: AdminEditorService
    ) {
        super(admin);
        this.changesVisible = false;
    }

    filter(filter: string, items: any[]): any[] {

        const matches = (f: string, item: any): boolean => {
            const [field, match] = f.split('=');
            if (!match) { return true; }
            const value = get(item, field) || '';
            if (match.includes('*')) {
                // User specified text search, be generous in matching.
                return escapeRqlValue(value).toLowerCase().includes(match.replace(/\*/g, '').toLowerCase());
            }
            // Chosen from predefined list, match exactly.
            return value.toLowerCase() === match.toLowerCase();
        };

        // The filter string could look like this, we need to support logical OR filters
        //  (entityType.id=simcard|entityType.id=asset),type.id=blah

        const filters =
            (filter || '').split(',')
                .filter(term => term)
                .map(term => {
                    if (term.includes('|')) {
                        return term.slice(1, term.length - 1).split('|');
                    }
                    return [term];
                });

        return filters.reduce(
            (result, column) => result.filter(item => column.some(term => matches(term, item))),
            items
        );
    }

    listEntities(ownerId: string, offset: number, limit: number, _sort: string, filter: string): Promise<EntityListResult> {

        return Promise.all([
            this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId), 60, true),
            getEntityTypes(this.admin, ownerId),
            getTypes(this.admin),
        ]).then(([company, entityTypes, types]) => {
            const items = [];
            Object.keys(company.customFields || {}).forEach(fieldType => {
                company.customFields[fieldType].filter(x => x['owner'] === company.type).forEach(field => {
                    items.push(CustomFieldDefinitionToCustomFieldResponse(entityTypes, types, ownerId, fieldType, field));
                });
            });
            return {
                title: this.admin.i18n.instant('ADMIN.ENTITIES.CUSTOMFIELDS'),
                type: 'customfield',
                count: items.length,
                limit: limit,
                offset: offset,
                items: this.filter(filter, items),
            };
        });

    }


    getEntity(id: string): Promise<CustomFieldResponse> {
        const [ownerId] = id.split('.');
        return this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId), 60, true).then(company => {
            return getCustomFieldResponseFromCompany(this.admin, company, id);
        });
    }

    createEntity(ownerId: string, entity: CustomFieldResponse): Promise<CustomFieldResponse> {
        return this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId), 60, true).then(company => {
            const field = CustomFieldResopnseToCustomFieldDefinition(entity);
            field.required = field.required ? true : false;
            field.values = field.values || [];
            const customFields = JSON.parse(JSON.stringify(company.customFields));
            const bucket = customFields[entity.entityType.id] || [];
            bucket.push(field);
            customFields[entity.entityType.id] = bucket;

            // check that we don't duplicate another id
            const count = Object.keys(customFields).reduce((cnt, key) => {
                return cnt + (countBy(customFields[key].map(x => x.id))[entity.tag] || 0);
            }, 0);
            if (count > 1) {
                throw new Error('A custom field with the same ID already exists');
            }

            return this.updateCompany(company, customFields).then(result => {
                return getCustomFieldResponseFromCompany(this.admin, result, `${ownerId}.${entity.entityType.id}.${entity.tag}`);
            });
        });
    }

    setEntityState(entity: CustomFieldResponse, state: string): Promise<CustomFieldResponse> {
        if (state === 'deleted') {
            const [companyId, entityType, id] = entity.id.split('.');
            return this.admin.getCached(`getCompany/${companyId}`, api => api.accounts.getCompany(companyId), 0).then(company => {
                const customFields = JSON.parse(JSON.stringify(company.customFields));
                const bucket = customFields[entityType] || [];
                const idx = bucket.findIndex(x => x.id === id);
                if (idx > -1) {
                    bucket.splice(idx, 1);
                }
                customFields[entityType] = bucket;
                return this.updateCompany(company, customFields).then(() => {
                    return entity;
                });
            });
        } else {
            return Promise.reject('Only deletion supported');
        }
    }

    updateCompany(company: CompanyResponse, customFields: CustomFields): Promise<CompanyResponse> {
        return UpdateCompanyCustomFields(this.admin, company, customFields);
    }

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {
        return Promise.all([
            getEntityTypes(this.admin, ownerId),
            getTypes(this.admin),
        ]).then(([entityTypes, types]) => {

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.CUSTOMFIELD.COLUMNS');
            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', ICONS.customfield),
                grid.IdNameComboColumn('entityType', 'ENTITY', entityTypes.map(x => ({ id: x.id, value: x.name }))),
                grid.IdNameComboColumn('type', 'TYPE', types.map(x => ({ id: x.id, value: x.name }))),
            ];
            return columns;
        });
    }

    getEditorSections(ownerId: string, _entity: CustomFieldResponse): Promise<SectionHandlerGroup<CustomFieldResponse>[]> {
        return Promise.resolve([{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new CustomFieldDetailsSectionHandler(this.admin, ownerId, false),
            ],
        }]);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: CustomFieldResponse, sections: FormSectionHandler<CustomFieldResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'customfield',
            entity: {} as any, // set up some defaults if you want
            sections: [
                new CustomFieldDetailsSectionHandler(this.admin, ownerId, true),
            ],
        });
    }

}
